import { useState, useEffect, useCallback } from 'react'
import { showToast } from '@/components/layout/Toast'
import { IconRobot, IconRefresh } from '@/components/icons/Icons'
import FlujoDelAgente from './FlujoDelAgente'

/**
 * El recepcionista de WhatsApp, editable sin entrar a la base.
 *
 * Hasta ahora sus cuatro textos y su horario vivían en `agente_config` y sólo se
 * cambiaban por SQL: el bot ya atendía clientes y nadie del equipo podía corregirle una
 * palabra. Esta pantalla es la razón de ser del grupo Comunicaciones.
 *
 * El interruptor se muestra pero **no se toca desde acá**: `RECEPCIONISTA_ACTIVO` es una
 * variable del contenedor, y un botón que prometiera apagarlo mentiría. Para callarlo en
 * una conversación puntual está «Tomar el control», que sí es inmediato.
 */

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

const PASOS: { clave: string; titulo: string; ayuda: string }[] = [
  { clave: 'saludo', titulo: 'Saludo', ayuda: 'Lo primero que recibe quien escribe por primera vez.' },
  { clave: 'necesidad', titulo: 'Qué necesita', ayuda: 'Usa {nombre} para tratarlo por su nombre.' },
  { clave: 'agenda', titulo: 'Horarios', ayuda: '{opciones} se reemplaza por los horarios libres reales.' },
  { clave: 'cierre_agendado', titulo: 'Cierre con reunión', ayuda: 'Admite {nombre}, {cuando} y {email}.' },
  { clave: 'cierre_sin_agenda', titulo: 'Cierre sin reunión', ayuda: '{cuando_responden} dice «en el día» o «mañana a primera hora» según el horario.' },
  { clave: 'entrega', titulo: 'Paso a un humano', ayuda: 'Se manda cuando pide precios o pide hablar con alguien.' },
]

interface Sesion {
  conversacion_id: number
  paso: string
  estado: string
  datos: Record<string, any>
  mensajes_enviados: number
  motivo_entrega: string | null
  updated_at: string
}

export default function RecepcionistaConfig() {
  const [textos, setTextos] = useState<Record<string, string>>({})
  const [horario, setHorario] = useState<{ inicio: string; fin: string }>({ inicio: '09:00', fin: '19:00' })
  const [sesiones, setSesiones] = useState<Sesion[]>([])
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)

  const token = () => localStorage.getItem('auth_token')

  const cargar = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/agentes/config`, {
        headers: { Authorization: `Bearer ${token()}` },
      })
      if (res.ok) {
        const data = await res.json()
        const fila = (Array.isArray(data) ? data : data.items ?? [])
          .find((a: any) => a.slug === 'recepcionista-whatsapp')
        if (fila) {
          setTextos(fila.mensajes_bienvenida ?? {})
          if (fila.horario) setHorario({ inicio: fila.horario.inicio ?? '09:00', fin: fila.horario.fin ?? '19:00' })
        }
      }

      const resSes = await fetch(`${API_URL}/api/chat/recepcionista/sesiones`, {
        headers: { Authorization: `Bearer ${token()}` },
      })
      if (resSes.ok) {
        const d = await resSes.json()
        setSesiones(d.items ?? [])
      }
    } catch {
      showToast('No se pudo cargar la configuración del recepcionista', 'error')
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => { void cargar() }, [cargar])

  const guardar = async () => {
    setGuardando(true)
    try {
      const res = await fetch(`${API_URL}/api/agentes/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          slug: 'recepcionista-whatsapp',
          mensajes_bienvenida: textos,
          horario: { ...horario, zona: 'America/Lima' },
        }),
      })
      if (!res.ok) throw new Error(await res.text())
      showToast('Textos del recepcionista guardados', 'success')
    } catch {
      showToast('No se pudo guardar', 'error')
    } finally {
      setGuardando(false)
    }
  }

  if (cargando) {
    return <div className="card"><div style={{ padding: 24, color: 'var(--text-secondary)' }}>Cargando…</div></div>
  }

  const activas = sesiones.filter(s => s.estado === 'activa')

  return (
    <div>
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconRobot size={18} /> Recepcionista de WhatsApp
          </h2>
        </div>

        <p style={{ color: 'var(--text-secondary)', fontSize: '0.86rem', margin: '0 0 16px' }}>
          Contesta <strong>sólo</strong> a quien escribe por primera vez y no está en la base.
          A los clientes conocidos y a los grupos no les dice nada. Para callarlo en una
          conversación puntual, usá «Tomar el control» desde el chat.
        </p>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Atiende desde</label>
            <input
              className="form-input"
              type="time"
              value={horario.inicio}
              onChange={e => setHorario(h => ({ ...h, inicio: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Hasta</label>
            <input
              className="form-input"
              type="time"
              value={horario.fin}
              onChange={e => setHorario(h => ({ ...h, fin: e.target.value }))}
            />
          </div>
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: -8 }}>
          Fuera de este horario <strong>contesta igual</strong> — la gracia es que nadie espere
          desde las 9 de la noche sin saber nada. Lo que cambia es qué promete el cierre.
        </p>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header"><h2>Qué dice en cada paso</h2></div>
        {PASOS.map(paso => (
          <div className="form-group" key={paso.clave}>
            <label className="form-label">{paso.titulo}</label>
            <textarea
              className="form-input"
              rows={3}
              value={textos[paso.clave] ?? ''}
              onChange={e => setTextos(t => ({ ...t, [paso.clave]: e.target.value }))}
            />
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>{paso.ayuda}</p>
          </div>
        ))}

        <button className="btn btn-primary" onClick={guardar} disabled={guardando} type="button">
          {guardando ? 'Guardando…' : 'Guardar textos'}
        </button>
      </div>

      <FlujoDelAgente
        slug="recepcionista-whatsapp"
        descripcion={<>
          Los pasos que recorre el bot. <strong>Guardar</strong> deja un borrador que
          sólo vos ves; el bot sigue con lo anterior hasta que pulses <strong>Publicar</strong>.
        </>}
      />

      <div className="card">
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2>Conversaciones atendidas</h2>
          <button className="btn btn-sm btn-secondary" onClick={() => void cargar()} type="button">
            <IconRefresh size={13} /> Actualizar
          </button>
        </div>

        {sesiones.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.86rem' }}>
            Todavía no atendió a nadie. Aparecerá acá en cuanto escriba un desconocido.
          </p>
        ) : (
          <>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', marginTop: 0 }}>
              {activas.length} en curso · {sesiones.length} en total
            </p>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Quién</th>
                    <th>Paso</th>
                    <th>Estado</th>
                    <th>Mensajes</th>
                  </tr>
                </thead>
                <tbody>
                  {sesiones.map(s => (
                    <tr key={s.conversacion_id}>
                      <td>
                        {s.datos?.nombre || '—'}
                        {s.datos?.empresa && (
                          <span style={{ color: 'var(--text-muted)' }}> · {s.datos.empresa}</span>
                        )}
                      </td>
                      <td>{s.paso}</td>
                      <td>
                        <span className={`badge badge-${s.estado === 'activa' ? 'info' : s.estado === 'completada' ? 'success' : 'warning'}`}>
                          {s.estado}
                        </span>
                        {s.motivo_entrega && (
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}> — {s.motivo_entrega}</span>
                        )}
                      </td>
                      <td>{s.mensajes_enviados}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
